import type { Constructor, Container, Operation } from '@packages/core/container';

import { CheckForCompletion } from './check-for-completion';

/** Default interval between polling attempts (5 seconds). */
const POLL_INTERVAL_MS = 5000;

/** Maximum time to wait before timing out (5 minutes). */
const MAX_WAIT_MS = 5 * 60 * 1000;

/** Parameters for waiting on asset processing to complete. */
export type WaitForAssetProcessingParams = {
    /** IDs of assets to wait for. */
    assetIds: string[];
};

/**
 * Poll asset processing statuses until all assets reach a terminal state
 * (complete or error) or a timeout is exceeded. This operation is awaitable;
 * the caller's spinner stays active until it resolves.
 *
 * Delegates each polling iteration to {@link CheckForCompletion}, which
 * fetches the latest status for each asset and categorizes them as settled
 * or still uploading.
 *
 * @throws {Error} if any assets have not settled within the timeout period
 */
export class WaitForAssetProcessing implements Operation {
    /**
     * Register this operation with its dependency on CheckForCompletion.
     *
     * @param container - the IoC container
     */
    static registerInContainer(container: Container): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- static registration requires cast
        container.register(this as any as Constructor, [CheckForCompletion]);
    }

    /** @param checkForCompletion - the single-iteration completion check operation */
    constructor(private checkForCompletion: CheckForCompletion) {}

    /**
     * Wait for all specified assets to finish processing.
     * Polls at a fixed interval until every asset reaches a terminal state
     * or the timeout is exceeded.
     *
     * @param params - the asset IDs to wait for
     * @throws {Error} when the timeout is exceeded with assets still processing
     */
    async exec(params: WaitForAssetProcessingParams): Promise<void> {
        const { assetIds } = params;

        if (!assetIds.length) {
            return;
        }

        let pendingIds = [...assetIds];
        const startTime = Date.now();

        while (pendingIds.length > 0) {
            // Check if we've exceeded the timeout
            const elapsed = Date.now() - startTime;
            if (elapsed >= MAX_WAIT_MS) {
                throw new Error(
                    `Asset processing timed out after ${Math.round(elapsed / 1000)} seconds. ` +
                        `${pendingIds.length} asset(s) still processing: ${pendingIds.join(', ')}`
                );
            }

            // Wait before the next poll
            await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

            // Check the current status of all pending assets
            const result = await this.checkForCompletion.exec({ uploadingAssetIds: pendingIds });

            // Update pending list to only those still uploading
            pendingIds = result.stillUploading.map((asset) => asset.id);
        }
    }
}
